import { useRef, useState } from "react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import { validateExpenseFile } from "../providers/commons/expenseFiles";
import type { CrmDataProvider } from "../providers/types";
import { EXPENSE_FILE_ACCEPT } from "./expenseScreenRules";
import type { ExpenseFileKind } from "./expenseTypes";

/**
 * Opens a blank tab synchronously with the click, then points it at the signed
 * URL once it resolves (a window opened after the `await` is blocked as a
 * popup). Files are never linked any other way: the bucket is private.
 */
const openSignedFile = async (fetchUrl: () => Promise<string>) => {
  const tab = window.open("", "_blank");
  try {
    const url = await fetchUrl();
    if (tab) tab.location.href = url;
  } catch (error) {
    tab?.close();
    throw error;
  }
};

/** "Abrir" button of a stored expense file; resolves a short-lived signed URL on click. */
export const ExpenseFileLink = ({
  path,
  label,
}: {
  path: string;
  label: string;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="min-h-11"
      aria-label={`${translate("crm.expenses.files.open")} ${label}`}
      onClick={() =>
        openSignedFile(() => dataProvider.getExpenseFileUrl(path)).catch(() =>
          notify("crm.expenses.files.open_error", { type: "error" }),
        )
      }
    >
      {translate("crm.expenses.files.open")}
    </Button>
  );
};

/**
 * One file picker of the expense form (comprobante, CFDI PDF or CFDI XML). The
 * file is validated (type and size), uploaded right away to the private bucket
 * under a NEW path, and the returned path is handed to the form; nothing is
 * ever replaced in storage.
 */
export const ExpenseFileField = ({
  id,
  kind,
  label,
  path,
  disabled,
  onChange,
}: {
  id: string;
  kind: ExpenseFileKind;
  label: string;
  path: string;
  disabled?: boolean;
  onChange: (path: string) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFile = async (file: File) => {
    const validation = validateExpenseFile(kind, file);
    if (!validation.valid) {
      notify(`crm.expenses.files.error_${validation.reason}`, {
        type: "error",
      });
      return;
    }
    setIsUploading(true);
    try {
      const uploaded = await dataProvider.uploadExpenseFile(kind, file);
      onChange(uploaded.path);
    } catch {
      notify("crm.expenses.files.error_upload", { type: "error" });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={EXPENSE_FILE_ACCEPT[kind]}
          className="sr-only"
          disabled={disabled || isUploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Reset so choosing the same rejected file again still fires onChange.
            event.target.value = "";
            if (file) void handleFile(file);
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          disabled={disabled || isUploading}
          onClick={() => inputRef.current?.click()}
        >
          {translate(
            path ? "crm.expenses.files.replace" : "crm.expenses.files.upload",
          )}
        </Button>
        {path ? (
          <>
            <span
              data-testid={`${id}-attached`}
              className="text-sm text-muted-foreground"
            >
              {translate("crm.expenses.files.attached")}
            </span>
            <ExpenseFileLink path={path} label={label} />
          </>
        ) : (
          <span className="text-sm text-muted-foreground">
            {translate("crm.expenses.files.none")}
          </span>
        )}
      </div>
    </div>
  );
};
